import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'

import useDimensions from '../../hooks/useDimensions'
import useGeoCheck from '../../hooks/useGeoCheck'
import useImageSize from '../../hooks/useImageSize'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { BonusList } from '../../types/free-bonus'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import LoginModal from '../common/log-in-modal'
import WalletManagementModal from '../wallet-management/wallet-management-modal'

const MoreMore = () => {
  const unified_balance = useBoundStore(state => state.user.unified_balance)
  const username = useBoundStore(state => state.user.username)
  const logInModal = useModal(LoginModal)
  const { geoCheck } = useGeoCheck()
  const walletManagementModal = useModal(WalletManagementModal)
  const navigate = useNavigate({ from: '/' })
  const { isDesktop } = useDimensions()
  const { t } = useTranslation('homeV2')
  const imageSize = useImageSize()
  const [hasBonus, setHasBonus] = useState(false)

  useEffect(() => {
    if (!username) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchHasBonus = async () => {
      try {
        const response = await api.post<ApiResponse<BonusList>>(
          '/ajax_data.php',
          {
            type: 'washcodeself_list',
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1) {
          setHasBonus(
            Object.keys(info).some(
              key => Number(info?.[key as keyof BonusList]) > 0,
            ),
          )
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchHasBonus()

    return () => {
      controller.abort()
    }
  }, [username])

  const activeCard = useMemo(() => {
    if (!username) return 0
    if (hasBonus) return 3
    if (+unified_balance > 0) return 2
    return 1
  }, [username, unified_balance, hasBonus])

  return (
    <div className="relative pt-[51px]">
      <img
        className="absolute w-[50px] top-[-30px] aspect-[156_/_251] right-0 xl:w-[120px] xl:right-0"
        src={imageSize('bell')}
        alt="Bell"
      />
      <h1 className="text-center font-Geometr706-BlkCn-BT text-txt text-3xl uppercase mb-[7px] px-4 py-0 xl:text-[35px]">
        {t('tour')}
      </h1>
      <h3 className="text-xs text-[#848292] font-Syncopate-Bold text-center uppercase mb-[29px] xl:text-[15px] xl:mb-11">
        {t('millions')}
      </h3>
      <div
        className="grid gap-[20px_10px] items-end mb-[60px] px-[30px] py-0 xl:items-center xl:max-w-screen-xl xl:gap-5 xl:mt-0 xl:mb-[60px] xl:mx-auto"
        style={{
          gridTemplateColumns: isDesktop
            ? [0, 1, 2, 3]
                .map(key => (key === activeCard ? '1.1fr' : '1fr'))
                .join(' ')
            : '1fr 1fr',
        }}>
        <div
          className={classNames(
            'text-white relative cursor-pointer rounded-[15px]',
            activeCard === 0
              ? 'w-[110%] translate-x-[-5%] bg-[none] p-0 xl:w-full xl:transform-none'
              : 'w-full bg-[linear-gradient(90deg,#ffffff,#787878)] p-px',
          )}
          onClick={() => {
            if (username) return
            geoCheck(() => navigate({ to: '/register' }))
          }}>
          <div
            className={classNames(
              'pb-3 px-2 rounded-[15px] xl:pb-3 xl:px-4',
              activeCard === 0
                ? 'h-[154px] bg-[linear-gradient(_136.23deg,#34ae18_3.02%,#84fc8e_54.63%,#6ae267_54.7%,#25b01a_73.49%,#1fad14_85.05%,#43af1a_94.49%_)] relative pt-[78px] xl:h-[270px] xl:pt-[137px]'
                : 'h-[148px] bg-[#2c223a] pt-5 xl:h-60 xl:pt-[45px]',
              `after:content-[''] after:absolute after:-translate-y-2/4 after:w-3.5 after:h-[82px] after:bg-[linear-gradient(90deg,#ffffff_0%,#787878_100%)] after:rounded-[15px_0_0_15px] after:-left-3.5 after:top-2/4`,
            )}>
            <div className="flx-ctr">
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1 ',
                  activeCard === 0
                    ? 'absolute text-[45px] left-[34px] top-5 xl:text-[80px] xl:left-[61px] xl:top-10'
                    : 'text-3xl xl:text-[50px]',
                )}>
                0
              </p>
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1',
                  activeCard === 0
                    ? 'absolute text-[45px] right-[34px] top-0 xl:text-[80px] xl:right-[61px] xl:top-0'
                    : 'text-3xl xl:text-[50px]',
                )}>
                1
              </p>
            </div>
            {activeCard === 0 && (
              <img
                className="absolute w-[115px] -translate-x-2/4 aspect-[520_/_397] left-2/4 -top-3 xl:w-[200px] xl:-top-5"
                src={imageSize('cherry')}
                alt="Cherry"
              />
            )}
            <p className="text-center text-[15px] font-Geometr706-BlkCn-BT uppercase mb-1.5 xl:text-3xl">
              {t('register')}
            </p>
            <p className="text-[9.5px] text-center xl:text-sm">
              {t('get-access')}
            </p>
          </div>
        </div>
        <div
          className={classNames(
            'text-white relative cursor-pointer rounded-[15px]',
            activeCard === 1
              ? 'w-[110%] translate-x-[-5%] bg-[none] p-0 xl:w-full xl:transform-none'
              : 'w-full bg-[linear-gradient(90deg,#ffffff,#787878)] p-px',
          )}
          onClick={() => {
            if (!username) {
              return geoCheck(logInModal.show)
            }

            TagManager.dataLayer({
              dataLayer: {
                event: 'deposit_form_open',
                event_category: 'deposit_funds',
                event_action: 'click_open',
                event_label: 'Deposit form Open',
                user_id: username,
              },
            })

            if (isDesktop) {
              return walletManagementModal.show({})
            }

            window.open(`${location.origin}/deposit`, 'blank')
          }}>
          <div
            className={classNames(
              'pb-3 px-2 rounded-[15px] xl:pb-3 xl:px-4',
              activeCard === 1
                ? 'h-[154px] bg-[linear-gradient(_136.23deg,#34ae18_3.02%,#84fc8e_54.63%,#6ae267_54.7%,#25b01a_73.49%,#1fad14_85.05%,#43af1a_94.49%_)] relative pt-[78px] xl:h-[270px] xl:pt-[137px]'
                : 'h-[148px] bg-[#2c223a] pt-5 xl:h-60 xl:pt-[45px]',
            )}>
            <div className="flx-ctr">
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1 ',
                  activeCard === 1
                    ? 'absolute text-[45px] left-[34px] top-5 xl:text-[80px] xl:left-[61px] xl:top-10'
                    : 'text-3xl xl:text-[50px]',
                )}>
                0
              </p>
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1',
                  activeCard === 1
                    ? 'absolute text-[45px] right-[34px] top-0 xl:text-[80px] xl:right-[61px] xl:top-0'
                    : 'text-3xl xl:text-[50px]',
                )}>
                2
              </p>
            </div>
            {activeCard === 1 && (
              <img
                className="absolute w-[115px] -translate-x-2/4 aspect-[520_/_397] left-2/4 -top-3 xl:w-[200px] xl:-top-5"
                src={imageSize('cherry')}
                alt="Cherry"
              />
            )}
            <p className="text-center text-[15px] font-Geometr706-BlkCn-BT uppercase mb-1.5 xl:text-3xl">
              {t('deposit')}
            </p>
            <p className="text-[9.5px] text-center xl:text-sm">
              {t('select-bonus')}
            </p>
          </div>
        </div>
        <div
          className={classNames(
            'text-white relative cursor-pointer rounded-[15px]',
            activeCard === 2
              ? 'w-[110%] translate-x-[-5%] bg-[none] p-0 xl:w-full xl:transform-none'
              : 'w-full bg-[linear-gradient(90deg,#ffffff,#787878)] p-px',
          )}
          onClick={() => navigate({ to: '/casino' })}>
          <div
            className={classNames(
              'pb-3 px-2 rounded-[15px] xl:pb-3 xl:px-4',
              activeCard === 2
                ? 'h-[154px] bg-[linear-gradient(_136.23deg,#34ae18_3.02%,#84fc8e_54.63%,#6ae267_54.7%,#25b01a_73.49%,#1fad14_85.05%,#43af1a_94.49%_)] relative pt-[78px] xl:h-[270px] xl:pt-[137px]'
                : 'h-[148px] bg-[#2c223a] pt-5 xl:h-60 xl:pt-[45px]',
            )}>
            <div className="flx-ctr">
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1 ',
                  activeCard === 2
                    ? 'absolute text-[45px] left-[34px] top-5 xl:text-[80px] xl:left-[61px] xl:top-10'
                    : 'text-3xl xl:text-[50px]',
                )}>
                0
              </p>
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1',
                  activeCard === 2
                    ? 'absolute text-[45px] right-[34px] top-0 xl:text-[80px] xl:right-[61px] xl:top-0'
                    : 'text-3xl xl:text-[50px]',
                )}>
                3
              </p>
            </div>
            {activeCard === 2 && (
              <img
                className="absolute w-[115px] -translate-x-2/4 aspect-[520_/_397] left-2/4 -top-3 xl:w-[200px] xl:-top-5"
                src={imageSize('cherry')}
                alt="Cherry"
              />
            )}
            <p className="text-center text-[15px] font-Geometr706-BlkCn-BT uppercase mb-1.5 xl:text-3xl">
              {t('play-and-win')}
            </p>
            <p className="text-[9.5px] text-center xl:text-sm">
              {t('options')}
            </p>
          </div>
        </div>
        <div
          className={classNames(
            'text-white relative cursor-pointer rounded-[15px]',
            activeCard === 3
              ? 'w-[110%] translate-x-[-5%] bg-[none] p-0 xl:w-full xl:transform-none'
              : 'w-full bg-[linear-gradient(90deg,#ffffff,#787878)] p-px',
          )}
          onClick={() => {
            if (!username) {
              return geoCheck(logInModal.show)
            }

            navigate({ to: '/profile/free-bonus' })
          }}>
          <div
            className={classNames(
              'pb-3 px-2 rounded-[15px] xl:pb-3 xl:px-4',
              activeCard === 3
                ? 'h-[154px] bg-[linear-gradient(_136.23deg,#34ae18_3.02%,#84fc8e_54.63%,#6ae267_54.7%,#25b01a_73.49%,#1fad14_85.05%,#43af1a_94.49%_)] relative pt-[78px] xl:h-[270px] xl:pt-[137px]'
                : 'h-[148px] bg-[#2c223a] pt-5 xl:h-60 xl:pt-[45px]',
              `after:content-[''] after:absolute after:-translate-y-2/4 after:w-3.5 after:h-[82px] after:top-2/4 after:bg-[linear-gradient(-90deg,#ffffff_0%,#787878_100%)] after:rounded-[0_15px_15px_0] after:left-[unset] after:-right-3.5`,
            )}>
            <div className="flx-ctr">
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1 ',
                  activeCard === 3
                    ? 'absolute text-[45px] left-[34px] top-5 xl:text-[80px] xl:left-[61px] xl:top-10'
                    : 'text-3xl xl:text-[50px]',
                )}>
                0
              </p>
              <p
                className={classNames(
                  'font-Avenir-Medium text-center mt-0 mb-2 mx-1',
                  activeCard === 3
                    ? 'absolute text-[45px] right-[34px] top-0 xl:text-[80px] xl:right-[61px] xl:top-0'
                    : 'text-3xl xl:text-[50px]',
                )}>
                4
              </p>
            </div>
            {activeCard === 3 && (
              <img
                className="absolute w-[115px] -translate-x-2/4 aspect-[520_/_397] left-2/4 -top-3 xl:w-[200px] xl:-top-5"
                src={imageSize('cherry')}
                alt="Cherry"
              />
            )}
            <p className="text-center text-[15px] font-Geometr706-BlkCn-BT uppercase mb-1.5 xl:text-3xl">
              {t('rewards')}
            </p>
            <p className="text-[9.5px] text-center xl:text-sm">
              {t('earn-money')}
            </p>
          </div>
        </div>
      </div>
      <div className="relative flex justify-center w-full mb-9">
        <img
          src={imageSize('slot-coins')}
          className="absolute -translate-y-2/4 w-[113px] aspect-[212_/_249] -left-2.5 top-2/4 xl:w-[250px]"
          alt="Slot coins"
        />
        <img
          src={imageSize('slot')}
          className="absolute -translate-y-2/4 w-[105px] aspect-[93_/_125] right-0 top-2/4 xl:w-[300px] xl:top-full"
          alt="Slot machine"
        />
        <Link
          className="text-xs font-Syncopate-Bold text-white bg-[#df0033] uppercase px-[21px] py-[19px] rounded-sm xl:text-[15px]"
          to="/casino/$category"
          params={{ category: 'lobby' }}>
          {t('know-the-games')}
        </Link>
      </div>
    </div>
  )
}

export default MoreMore
